/**
 * Shared utilities for the offline access cache used by ProtectedRoute and
 * BetaAcknowledgmentRouteGate. Centralised so the two readers stay in sync.
 *
 * Responsibilities:
 *  - Read/touch/write the current `v9` entry in localStorage.
 *  - Self-heal legacy `v2`..`v8` entries by migrating any still-valid
 *    `hasAccess: true` row forward to `v9` (and removing the old key).
 *  - Fire a lightweight telemetry beacon to `app_lifecycle_telemetry` so we
 *    can observe real-world offline cold-start outcomes post-launch.
 */

import { logger } from "@/lib/logger";

export type AccessCacheEntry = { hasAccess: boolean; checkedAt: number };

const DB_NAME = "lupuslogix-offline-access";
const DB_VERSION = 1;
const STORE_NAME = "access_grants";
const ACCESS_REVOKED_PREFIX = "llx:access-revoked:v1:";

const LEGACY_PREFIXES = [
  "llx:access:v2:",
  "llx:access:v3:",
  "llx:access:v4:",
  "llx:access:v5:",
  "llx:access:v6:",
  "llx:access:v7:",
  "llx:access:v8:",
];
export const CURRENT_ACCESS_CACHE_PREFIX = "llx:access:v9:";
export const ACCESS_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

let dbPromise: Promise<IDBDatabase> | null = null;

const openAccessDB = (): Promise<IDBDatabase> => {
  if (typeof indexedDB === "undefined") return Promise.reject(new Error("IndexedDB unavailable"));
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "userId" });
      }
    };
  });

  return dbPromise;
};

const safeParse = (raw: string | null): AccessCacheEntry | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AccessCacheEntry;
    if (
      typeof parsed?.hasAccess !== "boolean" ||
      typeof parsed?.checkedAt !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const isAccessRevoked = (userId: string): boolean => {
  try {
    return !!localStorage.getItem(`${ACCESS_REVOKED_PREFIX}${userId}`);
  } catch {
    return false;
  }
};

const writeIndexedAccessGrant = async (userId: string, entry: AccessCacheEntry): Promise<void> => {
  const db = await openAccessDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put({ userId, ...entry });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const readIndexedAccessGrant = async (userId: string): Promise<AccessCacheEntry | null> => {
  const db = await openAccessDB();
  const stored = await new Promise<(AccessCacheEntry & { userId: string }) | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(userId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  if (!stored?.hasAccess || typeof stored.checkedAt !== "number") return null;
  if (Date.now() - stored.checkedAt >= ACCESS_CACHE_TTL_MS) return null;
  return { hasAccess: true, checkedAt: stored.checkedAt };
};

const deleteIndexedAccessGrant = async (userId: string): Promise<void> => {
  const db = await openAccessDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(userId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

/**
 * If any legacy v2..v8 entry exists for this user with `hasAccess: true` and is
 * still within the 7-day window, promote it to the current v9 key. Always
 * removes legacy keys after inspecting them. Returns true if a migration
 * occurred.
 */
export const migrateLegacyAccessCache = (userId: string): boolean => {
  let migrated = false;
  try {
    // Don't re-migrate if a current v9 entry already exists.
    const current = safeParse(localStorage.getItem(`${CURRENT_ACCESS_CACHE_PREFIX}${userId}`));

    for (const prefix of LEGACY_PREFIXES) {
      const key = `${prefix}${userId}`;
      const legacy = safeParse(localStorage.getItem(key));
      if (!legacy) {
        // also clean stale keys with junk values
        localStorage.removeItem(key);
        continue;
      }

      if (
        !current &&
        legacy.hasAccess === true &&
        Date.now() - legacy.checkedAt < ACCESS_CACHE_TTL_MS
      ) {
        localStorage.setItem(
          `${CURRENT_ACCESS_CACHE_PREFIX}${userId}`,
          JSON.stringify({ hasAccess: true, checkedAt: legacy.checkedAt }),
        );
        migrated = true;
        logger.log("[offlineAccessCache] migrated legacy access cache forward", { from: prefix });
      }

      localStorage.removeItem(key);
    }
  } catch {
    /* ignore storage errors */
  }
  return migrated;
};

/**
 * Read the current v9 entry for a user, opportunistically migrating legacy
 * entries first. Returns the entry only when it is still inside the TTL and
 * indicates `hasAccess: true`.
 */
export const readValidAccessCache = (userId: string): AccessCacheEntry | null => {
  if (!userId) return null;
  if (isAccessRevoked(userId)) return null;
  migrateLegacyAccessCache(userId);
  try {
    const entry = safeParse(localStorage.getItem(`${CURRENT_ACCESS_CACHE_PREFIX}${userId}`));
    if (!entry) return null;
    if (entry.hasAccess !== true) return null;
    if (Date.now() - entry.checkedAt >= ACCESS_CACHE_TTL_MS) return null;
    return entry;
  } catch {
    return null;
  }
};

/**
 * Async IndexedDB-backed read used during installed PWA cold starts. Some
 * mobile browsers keep IndexedDB when localStorage is missing or slow, so this
 * restores the older offline behavior without widening access to other users.
 */
export const readValidAccessCacheAsync = async (userId: string): Promise<AccessCacheEntry | null> => {
  const localEntry = readValidAccessCache(userId);
  if (localEntry) return localEntry;
  if (!userId || isAccessRevoked(userId)) return null;

  try {
    const indexedEntry = await readIndexedAccessGrant(userId);
    if (!indexedEntry) return null;
    localStorage.setItem(`${CURRENT_ACCESS_CACHE_PREFIX}${userId}`, JSON.stringify(indexedEntry));
    return indexedEntry;
  } catch {
    return null;
  }
};

/**
 * Persist a verified allow decision after the online beta/legal gates pass.
 * This is the only state the offline gates use to render protected routes.
 */
export const writeAccessCache = (userId: string) => {
  if (!userId) return;
  const entry: AccessCacheEntry = { hasAccess: true, checkedAt: Date.now() };
  try {
    localStorage.removeItem(`${ACCESS_REVOKED_PREFIX}${userId}`);
    localStorage.setItem(
      `${CURRENT_ACCESS_CACHE_PREFIX}${userId}`,
      JSON.stringify(entry),
    );
  } catch {
    /* ignore */
  }
  void writeIndexedAccessGrant(userId, entry).catch(() => undefined);
};

export const revokeAccessCache = (userId: string) => {
  if (!userId) return;
  try {
    localStorage.setItem(`${ACCESS_REVOKED_PREFIX}${userId}`, Date.now().toString());
    localStorage.removeItem(`${CURRENT_ACCESS_CACHE_PREFIX}${userId}`);
  } catch {
    /* ignore */
  }
  void deleteIndexedAccessGrant(userId).catch(() => undefined);
};

/**
 * Re-stamp the `checkedAt` of an existing valid allow entry without doing a
 * network re-verification. Used on offline fast-path renders so the 7-day
 * window slides forward with real usage instead of expiring while the user is
 * mostly offline.
 */
export const touchAccessCache = (userId: string) => {
  if (!userId) return;
  if (isAccessRevoked(userId)) return;
  try {
    const existing = safeParse(localStorage.getItem(`${CURRENT_ACCESS_CACHE_PREFIX}${userId}`));
    if (!existing || existing.hasAccess !== true) return;
    const entry: AccessCacheEntry = { hasAccess: true, checkedAt: Date.now() };
    localStorage.setItem(
      `${CURRENT_ACCESS_CACHE_PREFIX}${userId}`,
      JSON.stringify(entry),
    );
    void writeIndexedAccessGrant(userId, entry).catch(() => undefined);
  } catch {
    /* ignore */
  }
};

export type OfflineBootstrapPath =
  | "online"
  | "offline_cache_hit"
  | "offline_cache_miss"
  | "offline_stalled"
  | "offline_no_session";

const TELEMETRY_DEDUPE_KEY = "llx:obs:last";

/**
 * Fire-and-forget beacon to app_lifecycle_telemetry capturing which offline
 * bootstrap path was taken on this load. Deduped per page-load so route
 * changes do not spam the table.
 */
export const logOfflineBootstrapPath = (
  path: OfflineBootstrapPath,
  details: Record<string, unknown> = {},
) => {
  try {
    const sentForLoad = sessionStorage.getItem(TELEMETRY_DEDUPE_KEY);
    if (sentForLoad === path) return;
    sessionStorage.setItem(TELEMETRY_DEDUPE_KEY, path);

    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
    if (!SUPABASE_URL || !SUPABASE_KEY) return;

    const payload = {
      event_type: "offline_bootstrap_path",
      details: {
        path,
        online: typeof navigator !== "undefined" ? navigator.onLine : null,
        is_standalone:
          typeof window !== "undefined" &&
          (window.matchMedia?.("(display-mode: standalone)").matches ||
            (navigator as Navigator & { standalone?: boolean }).standalone === true),
        ts_iso: new Date().toISOString(),
        ...details,
      },
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      url: typeof window !== "undefined" ? window.location.pathname : null,
    };

    const url = `${SUPABASE_URL}/rest/v1/app_lifecycle_telemetry`;
    const body = JSON.stringify(payload);
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${SUPABASE_KEY}`,
        Prefer: "return=minimal",
      },
      body,
    }).catch(() => {});
  } catch {
    /* never throw from telemetry */
  }
};